<template>
  <div v-if="allPromotions.length > 0 || isLoading || error" class="promotions-product row">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2 class="title-prod mb-0">{{ $t("home_client.promotions.title") }}</h2>
    </div>
    
    <div class="col col-lg-11 col-md-11 col-sm-11">
      <LoadingSpinner v-if="isLoading" min-height="200px" />
      
      <div v-else-if="error" class="alert alert-danger text-center">
        {{ $t("home_client.promotions.error") }}
      </div>
      
      <div v-else class="carousel__wrapper">
        <Carousel v-bind="carouselConfig">
          <Slide v-for="promotion in allPromotions" :key="promotion.id">
            <div class="promotion-card" :class="{ 'is-cumul': promotion.isCumul }">
              <div class="promotion-accent"></div>
              <div class="promotion-header">
                <div class="name-container">
                  <h3 class="promotion-name">{{ promotion.name }}</h3>
                </div>
                <span v-if="promotion.isCumul" class="cumul-badge">
                  <i class="ti ti-layers"></i> {{ $t("home_client.promotions.cumul") }}
                </span>
              </div>
              
              <div class="promotion-body">
                <div class="description-container">
                  <p v-if="promotion.descriptionOffre" class="promotion-description">
                    {{ promotion.descriptionOffre }}
                  </p>
                  <!-- <p v-else class="promotion-description">
                    {{ getPromotionDescription(promotion) }}
                  </p> -->
                </div>
              </div>

              <div class="promotion-footer">
                <div class="footer-top">
                  <div v-if="promotion.startsAt || promotion.endsAt" class="validity-badge">
                    <i class="ti ti-calendar"></i>
                    <span>{{ formatValidity(promotion) }}</span>
                  </div>
                </div>
               
              </div>
            </div>
          </Slide>
          
          <template #addons>
            <div class="carousel-nav-container">
              <Navigation />
            </div>
          </template>
        </Carousel>

      </div>
    </div>
  </div>
</template>

<script>
  import "vue3-carousel/carousel.css";
  import { Carousel, Slide, Navigation } from "vue3-carousel";
  import { getClientPromotions, getClientPromotionsCumul } from "@/api/offerveto";
  import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

  export default {
    name: "PromotionsCarousel",
    components: {
      Carousel,
      Slide,
      Navigation,
      LoadingSpinner,
    },
    data() {
      return {
        promotions: [],
        promotionsCumul: [],
        isLoading: false,
        error: null,
      };
    },
    computed: {
      allPromotions() {
        // Combine both promotion types and mark cumul promotions
        const regular = this.promotions.map(p => ({ ...p, isCumul: false }));
        const cumul = this.promotionsCumul.map(p => ({ ...p, isCumul: true }));
        
        // Filter out promotions where displayOffre is explicitly false
        return [...regular, ...cumul].filter(p => p.displayOffre !== false);
      },
      carouselConfig() {
        const hasEnoughItems = this.allPromotions.length >= 3;
        return {
          itemsToShow: Math.min(3, this.allPromotions.length),
          gap: 30,
          snapAlign: hasEnoughItems ? "start" : "center",
          autoplay: hasEnoughItems ? 5000 : 0,
          loop: hasEnoughItems,
          wrapAround: hasEnoughItems,
          pauseAutoplayOnHover: true,
          breakpoints: {
            0: {
              itemsToShow: 1,
              snapAlign: "center",
              loop: this.allPromotions.length > 1,
              wrapAround: this.allPromotions.length > 1,
            },
            600: {
              itemsToShow: Math.min(2, this.allPromotions.length),
              snapAlign: this.allPromotions.length >= 2 ? "start" : "center",
              loop: this.allPromotions.length >= 2,
              wrapAround: this.allPromotions.length >= 2,
            },
            992: {
              itemsToShow: Math.min(3, this.allPromotions.length),
              snapAlign: hasEnoughItems ? "start" : "center",
              loop: hasEnoughItems,
              wrapAround: hasEnoughItems,
            },
          },
        };
      },
    },
    methods: {
      async fetchPromotions() {
        this.isLoading = true;
        this.error = null;

        try {
          // Fetch both types of promotions concurrently
          const [regularResponse, cumulResponse] = await Promise.all([
            getClientPromotions(1, 50),
            getClientPromotionsCumul(1, 50),
          ]);

          this.promotions = regularResponse.promotions || [];
          this.promotionsCumul = cumulResponse.promotions || [];
        } catch (error) {
          console.error("Error fetching promotions:", error);
          this.error = error;
          this.promotions = [];
          this.promotionsCumul = [];
        } finally {
          this.isLoading = false;
        }
      },
      formatDiscount(promotion) {
        if (!promotion.actions || promotion.actions.length === 0) {
          return this.$t("home_client.promotions.special_offer");
        }

        const action = promotion.actions[0];
        const config = action.configuration;

        // Handle different action types
        switch (action.type) {
          case "order_percentage_discount":
            return `-${config?.percentage || 0}%`;
          
          case "order_fixed_discount":
            return `-${(config?.amount / 100 || 0).toFixed(2)}€`;
          
          case "unit_fixed_discount": {
            // Get the amount from the appropriate channel (esupplyvet, supplyvet, or certivet)
            const amount = config?.esupplyvet?.amount || config?.supplyvet?.amount || config?.certivet?.amount || 0;
            return `-${(amount / 100).toFixed(2)}€ ${this.$t("home_client.promotions.per_unit")}`;
          }
          
          case "parametres_declancheur":
            // Gift promotion - show what you get
            if (config?.article_gagne === "autre") {
              return `+${config?.quantite_gagnee || 1} ${this.$t("home_client.promotions.gift_product")}`;
            } else if (config?.article_gagne === "identique") {
              return `+${config?.quantite_gagnee || 1} ${this.$t("home_client.promotions.same_product")}`;
            }
            return this.$t("home_client.promotions.gift_offer");
          
          default:
            return this.$t("home_client.promotions.special_offer");
        }
      },
      getTriggerInfo(promotion) {
        // Get trigger information from rules
        if (!promotion.rules || promotion.rules.length === 0) {
          return null;
        }

        const rule = promotion.rules[0];
        const config = rule.configuration;

        if (rule.type === "notion_cumul" && config?.trigger_quantity) {
          const triggerType = config.trigger_type === "variant" 
            ? this.$t("home_client.promotions.products") 
            : this.$t("home_client.promotions.items");
          
          return `${this.$t("home_client.promotions.buy")} ${config.trigger_quantity} ${triggerType}`;
        }

        return null;
      },
      getPromotionDescription(promotion) {
        const triggerInfo = this.getTriggerInfo(promotion);
        const discountInfo = this.formatDiscount(promotion);
        
        if (triggerInfo) {
          return `${triggerInfo} bénéficiez de ${discountInfo}`;
        }
        
        return discountInfo;
      },
      formatValidity(promotion) {
        const start = promotion.startsAt ? new Date(promotion.startsAt).toLocaleDateString() : null;
        const end = promotion.endsAt ? new Date(promotion.endsAt).toLocaleDateString() : null;

        if (start && end) {
          return `${start} - ${end}`;
        } else if (end) {
          return this.$t("home_client.promotions.valid_until", { date: end });
        } else if (start) {
          return this.$t("home_client.promotions.valid_from", { date: start });
        }
        return this.$t("home_client.promotions.no_expiration");
      },
    },
    async mounted() {
      await this.fetchPromotions();
    },
  };
</script>

<style scoped>
  .promotions-product {
    gap: 10px;
    margin-top: 20px;
    justify-content: center;
  }

  .title-prod {
    color: #1e347d;
    font-weight: 900;
    line-height: 55px;
    font-size: 45px;
    font-style: normal;
    margin: 0;
    text-align: left;
  }

  .carousel__wrapper {
    padding: 2px;
    position: relative;
    margin: 0 auto;
  }

  .promotion-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(30, 52, 125, 0.1);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    min-height: 222px;
    border: 1px solid rgba(30, 52, 125, 0.08);
    position: relative;
    overflow: hidden;
    text-align: left;
  }


  .promotion-accent {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: #1e347d;
    opacity: 0.8;
  }

  .promotion-card.is-cumul .promotion-accent {
    background: #ff6b35;
  }



  .promotion-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(30, 52, 125, 0.2);
  }

  .promotion-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;
    gap: 12px;
  }

  .name-container {
    width: 100%;
  }

  .promotion-name {
    color: #6b7280;
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }


  .cumul-badge {
    background: #ff6b35;
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .promotion-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
  }

  .description-container {
    width: 100%;
  }

  .promotion-description {
    color: #374151;
    font-size: 15px;
    line-height: 1.5;
    margin: 0;
    font-weight: 500;
  }

  .promotion-custom-description {
    color: #1e347d;
    font-size: 18px;
    line-height: 1.4;
    margin: 0;
    font-weight: 800;
  }

  .promotion-footer {
    margin-top: auto;
    padding-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .footer-top {
    display: flex;
    align-items: center;
  }

  .validity-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #1e347d;
    background: #f0f2f9;
    padding: 4px 10px;
    border-radius: 6px;
    font-weight: 600;
  }

  .validity-badge i {
    font-size: 14px;
  }

  .footer-bottom {
    display: flex;
    justify-content: flex-end;
  }

  .action-hint {
    font-size: 13px;
    font-weight: 700;
    color: #1e347d;
    display: flex;
    align-items: center;
    gap: 6px;
    opacity: 0.7;
    transition: opacity 0.2s ease, transform 0.2s ease;
  }

  .promotion-card:hover .action-hint {
    opacity: 1;
    transform: translateX(4px);
  }



  .validity-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    color: #666;
  }

  .validity-info i {
    font-size: 16px;
  }

  /* Carousel navigation styling - matching IncontournablesProduct */
  .carousel {
    --vc-nav-background: rgba(255, 255, 255, 0.7);
    --vc-nav-border-radius: 100%;
  }

  :deep(.carousel__track) {
    align-items: stretch;
  }

  :deep(.carousel__slide) {
    padding: 10px;
    height: auto;
  }


  :deep(.carousel__icon) {
    fill: #1e347d;
    height: 70%;
  }

  :deep(.carousel__next),
  :deep(.carousel__prev) {
    top: 38%;
    border: 1px solid #1e347d !important;
    background: #eff1f7;
  }

  :deep(.carousel__next:hover),
  :deep(.carousel__prev:hover) {
    background: #c9cfde;
  }

  :deep(.carousel__prev) {
    left: -4% !important;
  }

  :deep(.carousel__next) {
    right: -4% !important;
  }

  /* Responsive styles - matching IncontournablesProduct */
  @media (max-width: 992px) {
    .promotions-product {
      margin-top: 10px;
      gap: 6px;
      padding-left: 8px;
      padding-right: 8px;
    }

    .title-prod {
      font-size: 22px;
      line-height: 32px;
      margin-bottom: 10px;
    }

    .promotion-card {
      padding: 20px;
      min-height: 200px;
    }


    .promotion-name {
      font-size: 16px;
    }

    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
    }
  }

  @media (max-width: 768px) {
    .promotions-product {
      margin-top: 4px;
      gap: 3px;
      padding-left: 2px;
      padding-right: 2px;
    }

    .title-prod {
      font-size: 14px;
      line-height: 30px;
    }

    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
    }

    .promotion-card {
      padding: 16px;
      min-height: 180px;
    }


    .promotion-name {
      font-size: 15px;
    }

    .cumul-badge {
      font-size: 11px;
      padding: 3px 10px;
    }

    .promotion-description {
      font-size: 13px;
    }

    .validity-info {
      font-size: 12px;
    }

    :deep(.carousel__prev),
    :deep(.carousel__next) {
      width: 30px;
      height: 30px;
    }
  }

  @media (max-width: 600px) {
    .promotions-product {
      margin-top: 4px;
      gap: 3px;
      padding-left: 2px;
      padding-right: 2px;
    }

    .title-prod {
      font-size: 15px;
      line-height: 22px;
      margin-bottom: 6px;
    }

    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
    }

    .promotion-card {
      padding: 14px;
    }


    .promotion-name {
      font-size: 14px;
    }

    .cumul-badge {
      font-size: 10px;
      padding: 2px 8px;
    }

    .promotion-description {
      font-size: 12px;
    }
  }
</style>
